/**
 * PromptConfigPanel — V14.85 : vue « config du prompt chargé » du lanceur.
 * Remplace la « Surcharge ponctuelle » : chaque variable réellement injectée
 * dans le prompt est listée avec son origine (config projet, lanceur,
 * Matériauthèque, défaut typologie, étage du lot) et ajustable en place.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Loader2, Lock, Save, Eye, Sparkles } from 'lucide-react';
import { useRefsChanged } from '../../lib/generationEvents';
import StageScenarioCard from './StageScenarioCard';
import { ORIGIN_STYLE, OriginChip, VariableRow } from './PromptVariableRow';

export { ORIGIN_STYLE, OriginChip, VariableRow };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DERIVED_ROWS = (d) => [
  { id: 'typologie', label: 'Typologie', value: d.project_type || 'apartment', source: 'Fiche projet', hint: 'Paramètres du projet' },
  { id: 'geometrie', label: 'Géométrie tracée', value: d.locked ? `${d.walls_count} murs · verrouillée (${d.lock_source})` : 'Aucun tracé — mode descriptif', source: 'Éditeur Plan V2', hint: 'Éditeur Plan → Structure' },
  { id: 'materiautheque', label: 'Matériauthèque', value: d.materials_brief_present ? 'Incluse (CCTP / photos validés)' : 'Aucune — texte libre', source: 'Matériauthèque', hint: 'Matériauthèque' },
  { id: 'whitebox_ref', label: 'Maquette réf.', value: d.whitebox_reference_job_id ? `✓ ${d.whitebox_reference_vue_angle || ''} (${d.whitebox_reference_job_id.slice(-6)})` : 'Aucune — lancez « 1 · Maquette blanche » puis ancrez-la dans la galerie', source: 'Galerie (ancre)', hint: 'Galerie → bouton Référence' },
  { id: 'textured_ref', label: 'Texturé réf.', value: d.textured_reference_job_id ? `✓ (${d.textured_reference_job_id.slice(-6)})` : 'Aucun — lancez « 2 · Texturer » puis ancrez-le', source: 'Galerie (ancre)', hint: 'Galerie → bouton Référence' },
  { id: 'furnished_ref', label: 'Meublé réf.', value: d.furnished_reference_job_id ? `✓ (${d.furnished_reference_job_id.slice(-6)})` : 'Aucun — validez un rendu « 3 · Meubler »', source: 'Galerie (validation)', hint: 'Galerie → Valider' },
  { id: 'niveau', label: 'Niveau', value: d.floor_name || (d.floor_level === null || d.floor_level === undefined ? '—' : `Niveau ${d.floor_level}`), source: 'Étage du lot', hint: 'Éditeur Plan' },
];

const PromptConfigPanel = ({ projectId, lotId, params, onParamChange, onResetParam, onOpenFullPrompt, onSavedConfig, onLoaded, children }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set(['Projet', 'Environnement', 'Style']));
  const timer = useRef(null);
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const paramsKey = JSON.stringify(params || {});
  // V14.93 — recharge quand une référence d'étape change (validation / ancre / refus dans la galerie).
  const [refsTick, setRefsTick] = useState(0);
  useRefsChanged(useCallback((d) => { if (!d.lotId || d.lotId === lotId) setRefsTick((t) => t + 1); }, [lotId]));

  useEffect(() => {
    if (!lotId) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.post(`${API}/projects/${projectId}/generation/preview-prompt`,
          { lot_id: lotId, params: JSON.parse(paramsKey) }, { withCredentials: true });
        setData(d);
        onLoadedRef.current?.({ whitebox_reference_job_id: d.whitebox_reference_job_id, whitebox_reference_vue_angle: d.whitebox_reference_vue_angle, textured_reference_job_id: d.textured_reference_job_id, furnished_reference_job_id: d.furnished_reference_job_id, locked: d.locked, materials_brief_present: d.materials_brief_present });
      } catch (e) {
        toast.error('Config du prompt indisponible', { description: e?.response?.data?.detail || e?.message });
      } finally {
        setLoading(false);
      }
    }, 450);
    return () => clearTimeout(timer.current);
  }, [projectId, lotId, paramsKey, refsTick]);

  const stageKeys = useMemo(() => new Set(data?.scenario?.keys || []), [data]);
  const groups = useMemo(() => {
    const g = new Map();
    (data?.variables || []).forEach((v) => { if (!g.has(v.group)) g.set(v.group, []); g.get(v.group).push(v); });
    const entries = Array.from(g.entries());
    if (!stageKeys.size) return entries;
    // Groupes contenant des variables de l'étape en premier.
    return entries.sort((a, b) => (b[1].some((v) => stageKeys.has(v.key)) ? 1 : 0) - (a[1].some((v) => stageKeys.has(v.key)) ? 1 : 0));
  }, [data, stageKeys]);

  // Ouvre automatiquement les groupes concernés par l'étape courante.
  useEffect(() => {
    if (!stageKeys.size) return;
    setOpenGroups((s) => {
      const n = new Set(s);
      groups.forEach(([group, vars]) => { if (vars.some((v) => stageKeys.has(v.key))) n.add(group); });
      return n;
    });
  }, [stageKeys, groups]);

  const adjusted = (data?.variables || []).filter((v) => v.origin === 'lanceur').length;

  const saveAsProjectConfig = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const values = {};
      data.variables.forEach((v) => { if (v.editable) values[v.key] = v.value; });
      const cur = await axios.get(`${API}/projects/${projectId}/prompt-config`, { withCredentials: true });
      await axios.put(`${API}/projects/${projectId}/prompt-config`,
        { values: { ...(cur.data?.values || {}), ...values }, template: cur.data?.template }, { withCredentials: true });
      toast.success('Config projet enregistrée', { description: `${Object.keys(values).length} variables — s'applique aux prochaines générations.` });
      onSavedConfig?.(values);
    } catch (e) {
      toast.error('Enregistrement impossible', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setSaving(false);
    }
  };

  if (!lotId) {
    return <p className="text-[11px]" style={{ color: 'var(--c-text-faint)' }}>Choisis un lot pour charger la configuration du prompt.</p>;
  }

  return (
    <div className="rounded-lg p-2.5 space-y-2" style={{ background: 'rgba(96,165,250,0.05)', border: '1px solid rgba(96,165,250,0.18)' }} data-testid="prompt-config-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="hud-eyebrow">Prompt chargé</span>
          {loading && <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--c-text-muted)' }} />}
          {data && (
            <>
              <span className="text-[10px] px-2 py-0.5 rounded-full" data-testid="prompt-config-scope"
                    style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--c-text-2)', border: '1px solid var(--c-border)' }}>
                {data.config_scope === 'project' ? 'Config projet' : 'Config par défaut (globale)'}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid="prompt-config-mode"
                    style={data.locked
                      ? { background: 'rgba(14,165,233,0.12)', color: '#38BDF8', border: '1px solid rgba(14,165,233,0.35)' }
                      : { background: 'rgba(148,163,184,0.12)', color: '#94A3B8', border: '1px solid rgba(148,163,184,0.30)' }}>
                <Lock className="w-2.5 h-2.5" /> {data.locked ? 'Géométrie verrouillée' : 'Mode descriptif'}
              </span>
              <span className="text-[10px]" style={{ color: 'var(--c-text-faint)' }}>{data.prompt_chars} car.{adjusted ? ` · ${adjusted} ajustée${adjusted > 1 ? 's' : ''} ici` : ''}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={onOpenFullPrompt} disabled={!data}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded hover:opacity-80 disabled:opacity-40"
                  style={{ color: 'var(--c-gold)', border: '1px solid rgba(217,160,91,0.35)' }} data-testid="prompt-config-open-full">
            <Eye className="w-3 h-3" /> Prompt complet
          </button>
          <button type="button" onClick={saveAsProjectConfig} disabled={!data || saving || !adjusted}
                  title={adjusted ? 'Enregistre les ajustements comme nouvelle config projet' : 'Aucun ajustement à enregistrer'}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded hover:opacity-80 disabled:opacity-40"
                  style={{ color: '#DBEAFE', background: 'rgba(96,165,250,0.18)', border: '1px solid rgba(96,165,250,0.4)' }} data-testid="prompt-config-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Enregistrer dans la config projet
          </button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1" data-testid="prompt-config-derived">
          {DERIVED_ROWS(data).map((r) => (
            <div key={r.id} className="flex items-baseline gap-1.5 text-[11px] min-w-0" title={`Source : ${r.source} — modifier via ${r.hint}`}>
              <span className="uppercase tracking-wider text-[9.5px] shrink-0" style={{ color: 'var(--c-text-faint)' }}>{r.label}</span>
              <span className="truncate" style={{ color: 'var(--c-text-2)' }}>{r.value}</span>
              <Sparkles className="w-2.5 h-2.5 shrink-0" style={{ color: '#5EEAD4' }} />
            </div>
          ))}
        </div>
      )}

      {data?.scenario && (
        <StageScenarioCard scenario={data.scenario} variables={data.variables || []} />
      )}

      {groups.map(([group, vars]) => {
        const open = openGroups.has(group);
        const nAdj = vars.filter((v) => v.origin === 'lanceur').length;
        const nStage = stageKeys.size ? vars.filter((v) => stageKeys.has(v.key)).length : 0;
        return (
          <div key={group} data-testid={`prompt-config-group-${group}`}>
            <button type="button" className="w-full flex items-center gap-1.5 text-[11px] py-1 hover:opacity-80"
                    style={{ color: 'var(--c-text)' }}
                    onClick={() => setOpenGroups((s) => { const n = new Set(s); n.has(group) ? n.delete(group) : n.add(group); return n; })}
                    data-testid={`prompt-config-group-toggle-${group}`}>
              {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              <span className="uppercase tracking-wider text-[10px]">{group}</span>
              <span className="text-[10px]" style={{ color: 'var(--c-text-faint)' }}>· {vars.length} variable{vars.length > 1 ? 's' : ''}{nAdj ? ` · ${nAdj} ajustée${nAdj > 1 ? 's' : ''}` : ''}</span>
              {nStage > 0 && (
                <span className="text-[9px] px-1.5 py-px rounded-full font-semibold" style={{ background: 'rgba(217,160,91,0.16)', color: 'var(--c-gold)', border: '1px solid rgba(217,160,91,0.35)' }}
                      data-testid={`prompt-config-group-stage-${group}`}>
                  étape {data?.scenario?.n} · {nStage}
                </span>
              )}
            </button>
            {open && vars.map((v) => (
              <VariableRow key={v.key} v={v} onChange={onParamChange} onReset={onResetParam}
                           inStage={stageKeys.size ? stageKeys.has(v.key) : undefined} />
            ))}
          </div>
        );
      })}

      {children}
    </div>
  );
};

export default PromptConfigPanel;
